import { Tooltip } from '@mui/material';
import cn from 'classnames';
import * as React from 'react';

import { ReactComponent as HiddenIcon } from '../../assets/images/icon-invisible.svg';
import { ReactComponent as MultipleOptionsIcon } from '../../assets/images/icon-msku.svg';
import { ReactComponent as PreorderIcon } from '../../assets/images/icon-preorder-allowed.svg';

import {
  HIDDEN_FOR_RECIPIENT_HINT_TEXT,
  MSKU_HAS_ITEMS_FROM_DIFFERENT_FC_WARNING,
  MSKU_ITEM_ICON_HINT,
  PREORDER_ITEM_HINT_TEXT,
} from '../../constants/inventories';
import useModal from '../../hooks/useModal';
import { IInventoryItem, InventoryTypesEnum } from '../../types/inventories';
import { isBrokenMSKU, isPreorderItem } from '../../utils/inventories';
import { CountIndicator, CustomizableItemIndicator, MskuDetailsModal } from '../index';

import styles from './ItemMetadata.module.scss';

interface IProps {
  className?: string;
  showCount?: boolean;
  item: {
    item_id?: string;
    name?: string;
    type: InventoryTypesEnum;
    count?: number;
    sku_options?: IInventoryItem[];
    fulfillment_center_id?: string;
    item_customization_allowed?: boolean;
    is_hidden_for_recipient?: boolean;
    preorder_allowed?: boolean;
    low_stock_threshold?: number;
  };
}

const ItemMetadata = ({ className, showCount = true, item }: IProps) => {
  const { openModal, closeModal, Modal } = useModal();
  const {
    item_id: itemId,
    name: title,
    type,
    sku_options: skuOptions,
    fulfillment_center_id: fulfillmentCenterId,
    count,
    item_customization_allowed: isCustomizable,
    is_hidden_for_recipient: isHidden,
    preorder_allowed: isPreorder,
    low_stock_threshold: threshold,
  } = item;

  const handleShowMskuModal = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      openModal(skuOptions);
    },
    [skuOptions, openModal],
  );

  const isMsku = React.useMemo(() => type === InventoryTypesEnum.MSKU, [type]);

  const shouldShowMskuWarning = React.useMemo(
    () =>
      fulfillmentCenterId &&
      skuOptions &&
      isBrokenMSKU({ fulfillment_center_id: fulfillmentCenterId, sku_options: skuOptions || [] }),
    [fulfillmentCenterId, skuOptions],
  );

  const renderMskuDetailsModal = React.useMemo(
    () => (
      <Modal className={styles.multipleOptionsDetailsModal}>
        {(skuOptionsArray: IInventoryItem[]) =>
          skuOptionsArray && skuOptionsArray.length ? (
            <MskuDetailsModal
              parent={item as IInventoryItem}
              onClose={closeModal}
              skuOptions={skuOptionsArray}
              name={title || ''}
            />
          ) : (
            <React.Fragment />
          )
        }
      </Modal>
    ),
    [itemId, closeModal, Modal, title],
  );

  return (
    <div
      className={cn(styles.container, className)}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
    >
      {showCount && (
        <CountIndicator
          type={type}
          count={count}
          skuOptions={skuOptions}
          isPreorderAllowed={isPreorder}
          threshold={threshold}
        />
      )}
      {isMsku && (
        <Tooltip
          title={shouldShowMskuWarning ? MSKU_HAS_ITEMS_FROM_DIFFERENT_FC_WARNING : MSKU_ITEM_ICON_HINT}
          arrow
          placement="top"
        >
          <div onClick={handleShowMskuModal} className={cn(styles.mskuDetails, styles.scalable)} role="button">
            <MultipleOptionsIcon className={cn(styles.mskuIcon, { [styles.alert]: shouldShowMskuWarning })} />
          </div>
        </Tooltip>
      )}
      {isPreorderItem(item as IInventoryItem) && (
        <Tooltip title={PREORDER_ITEM_HINT_TEXT} arrow placement="top">
          <div className={cn(styles.preorderDetails, styles.scalable)}>
            <PreorderIcon className={cn(styles.preorderIcon)} />
          </div>
        </Tooltip>
      )}
      {isCustomizable && <CustomizableItemIndicator className={styles.scalable} />}
      {isHidden && (
        <Tooltip title={HIDDEN_FOR_RECIPIENT_HINT_TEXT} arrow placement="top">
          <div className={styles.hiddenIconTooltip}>
            <HiddenIcon className={cn(styles.scalable, styles.hiddenInfo)} />
          </div>
        </Tooltip>
      )}
      {isMsku && renderMskuDetailsModal}
    </div>
  );
};

export default ItemMetadata;
